/**
 * PrijzenPage — IMPERIA AI prijsstructuur (eerlijk en consistent).
 *
 * 3 tiers + vergelijkingstabel + Backlinks-preview-widget + FAQ + CTA.
 * SEO: H1 met "Prijzen", H2 per tier, schema.org Product met alle 4 offers.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import BacklinksPreviewWidget from '../components/BacklinksPreviewWidget';
import {
  ArrowRight, CheckCircle2, X, Crown, Sparkles, Bot,
  Headphones, ShieldCheck, ChevronRight, Info, Star, Zap,
} from 'lucide-react';

const VAT_LINE = 'Alle prijzen exclusief btw. Maandelijks opzegbaar. Geen lock-in.';

/* ─── 3 tiers — single source of truth ─── */
const TIERS = [
  {
    id: 'free',
    label: 'Gratis Online Scan',
    price: '€0',
    period: '60 seconden',
    tagline: 'Direct inzicht in uw online vindbaarheid — zonder verplichtingen.',
    bullets: [
      'Volledige online groeiscan in < 60 seconden',
      'Top-10 verbeterpunten met prioritering',
      'Concurrentenanalyse + lokale ranking',
      '3 backlink-kansen voor uw branche',
      'Persoonlijke groeiscore (0-100)',
      'Geen creditcard nodig',
    ],
    cta: { label: 'Start gratis scan', href: '/register' },
    accent: '#64748b',
    voorWie: 'Iedereen die wil weten hoe Google zijn site ziet — zonder commitment.',
  },
  {
    id: 'self',
    label: 'Connected Self-Service',
    price: '€14,95',
    period: 'p/m excl. btw',
    tagline: 'Live dashboard + AI-suggesties — u voert zelf uit met stap-voor-stap guides.',
    bullets: [
      'Real-time dashboard met live data per dienst',
      'AI-suggesties per opdracht (Huidig → Beter)',
      'Stap-voor-stap guides per CMS',
      'Koppel WordPress · Shopify · GBP · GA4',
      'Voortgang per dienst meetbaar',
      '80+ backlink-directories beschikbaar',
      'CSV-export voor uw boekhouder',
      'E-mail support binnen 24 uur',
    ],
    cta: { label: 'Start Self-Service', href: '/register' },
    accent: '#2563eb',
    voorWie: 'Ondernemers die zelf doen, maar wel willen weten welke 3 dingen vandaag het meeste opleveren.',
  },
  {
    id: 'managed',
    label: 'Managed AI Bot',
    price: '€995,95',
    period: 'excl. btw',
    secondary: 'Eerste maand. Daarna €2.495,95 p/m excl. btw.',
    tagline: 'AI Bot voert ECHT uit. U keurt strategisch goed. Wij garanderen meetbare groei.',
    bullets: [
      'Alles uit Connected Self-Service inbegrepen',
      'AI Bot voert WordPress/Shopify-fixes direct uit',
      'Snippet-generatie voor Wix · Squarespace · Webador',
      'Ops-team plaatst handmatig in Joomla/Drupal (SLA 5d)',
      'Google Ads · Meta · TikTok · Snapchat-beheer',
      '25-40 nieuwe backlinks per maand (branche-specifiek)',
      'AI content & blog publicatie (4-8 artikelen/mnd)',
      'Website redesign of nieuwe site bouwen',
      'Conversie-optimalisatie + A/B-tests',
      'Maandelijks rapport + strategiegesprek (30 min)',
      'Prioriteit support < 4 uur',
    ],
    cta: { label: 'Aan de slag met Managed AI', href: '/register' },
    accent: '#D4A017',
    popular: true,
    voorWie: 'Ondernemers die marketing willen uitbesteden zonder een agency met 2-jaarscontracten en vage rapportages.',
  },
];

/* ─── Vergelijkingstabel — eerlijk per feature ─── */
const COMPARE_ROWS = [
  { feature: 'Online groeiscan', free: 'Eenmalig', self: 'Continu', managed: 'Continu' },
  { feature: 'Top-10 verbeterpunten', free: '✓', self: 'Alle issues', managed: 'Alle issues' },
  { feature: 'AI-suggesties per opdracht', free: '–', self: '✓', managed: '✓' },
  { feature: 'Stap-voor-stap guides', free: '–', self: '✓', managed: '✓' },
  { feature: 'WordPress/Shopify auto-fixes', free: '–', self: '–', managed: '✓ via REST API' },
  { feature: 'Wix/Squarespace snippets', free: '–', self: '–', managed: '✓ kant-en-klaar' },
  { feature: 'Joomla/Drupal handmatig', free: '–', self: '–', managed: '✓ ops-team (5d SLA)' },
  { feature: 'Google Ads beheer', free: '–', self: 'Templates', managed: 'Volledig beheer' },
  { feature: 'Meta · TikTok · Snapchat Ads', free: '–', self: 'Templates', managed: 'Volledig beheer' },
  { feature: 'Google Bedrijfsprofiel', free: 'Audit', self: 'Optimalisatie-tips', managed: 'Posts + reviews' },
  { feature: 'Backlinks per maand', free: '3 kansen', self: 'Outreach-templates', managed: '25-40 nieuwe' },
  { feature: 'AI content (blog)', free: '–', self: 'AI-suggesties', managed: '4-8 artikelen/maand' },
  { feature: 'Website redesign', free: '–', self: '–', managed: '✓ inbegrepen' },
  { feature: 'Nieuwe website laten maken', free: '–', self: '–', managed: '✓ inbegrepen' },
  { feature: 'Conversie-optimalisatie', free: 'Top-3', self: 'Audit', managed: 'Maandelijkse iteraties' },
  { feature: 'Maandelijks rapport', free: '–', self: 'Dashboard', managed: '+ strategiegesprek 30 min' },
  { feature: 'Support', free: 'Community', self: 'E-mail 24u', managed: 'Prioriteit < 4u' },
];

const FAQ = [
  {
    q: 'Wat is het verschil tussen Self-Service en Managed AI?',
    a: 'Self-Service (€14,95 p/m) geeft u alle inzichten + AI-suggesties — maar u voert zelf de wijzigingen uit. Bij Managed AI (€995,95 launch / €2.495,95 recurring) voert onze AI bot de fixes daadwerkelijk uit via de WordPress/Shopify REST API, of via snippets voor Wix/Squarespace, of via ons ops-team voor Joomla/Drupal.',
  },
  {
    q: 'Waarom is de eerste maand van Managed AI goedkoper (€995,95)?',
    a: 'De eerste maand is een "launch-maand" met intensieve setup: kanaalkoppelingen, basis-audit, fix-doorvoering en strategie-gesprek. Daarna gaat het automatische, structurele werk in — €2.495,95 per maand inclusief alle uitvoering, ads-beheer, 25-40 backlinks en content. Beide maandelijks opzegbaar.',
  },
  {
    q: 'Krijg ik écht 25-40 backlinks per maand bij Managed AI?',
    a: 'Ja. Wij hebben 80+ Nederlandse directories in onze catalog (35 algemeen + 46 branche-specifiek). AI prioriteert per maand de hoogste-DA en meest branche-relevante listings. Restaurants krijgen Iens/TheFork/Eet.nu, bouwers krijgen Werkspot/HomeDeal/Bouwwereld, zorgverleners krijgen ZorgkaartNL/Treatwell.',
  },
  {
    q: 'Mijn website draait op Wix/Squarespace/Webador. Werkt dit?',
    a: 'Ja. Voor deze CMS\'en (zonder schrijf-API) genereert onze AI bot kant-en-klare snippets (HTML-blokken, meta-tags, content) met heldere plaats-instructies — "Site-instellingen → SEO Basics" voor Wix bijvoorbeeld. U plakt het in 30 seconden. Voor Joomla/Drupal/Mijndomein plaatst ons ops-team handmatig binnen 5 werkdagen.',
  },
  {
    q: 'Kan ik wisselen van Self-Service naar Managed AI?',
    a: 'Ja, op elk moment. U behoudt al uw data en historie. Andersom kan ook — van Managed naar Self-Service of zelfs naar de gratis tier.',
  },
  {
    q: 'Is er een minimumcontract?',
    a: 'Nee. Alle tiers zijn maandelijks opzegbaar. Geen jaarcontracten, geen lock-in. Wij leveren waarde of u stopt — zo simpel.',
  },
  {
    q: 'Wat als AI iets fouts doet?',
    a: 'Wij maken voor élke wijziging een backup. U kunt elke actie terugdraaien in het dashboard. Daarnaast loggen wij élke wijziging met evidence-URL zodat u live kunt verifiëren wat er is gebeurd. En u keurt strategische beslissingen vooraf goed — AI voert alleen approved fixes uit.',
  },
  {
    q: 'Kan ik met Stripe betalen?',
    a: 'Ja. Wij verwerken alle betalingen via Stripe — de wereldwijde standaard voor veilige online betalingen. SEPA-incasso, iDEAL, creditcard, Bancontact — u kiest. Facturatie en btw-administratie wordt automatisch geregeld.',
  },
  {
    q: 'Wat als ik gewoon eerst wil zien wat het oplevert?',
    a: 'Start met de gratis online scan — geen creditcard, geen verplichtingen. U krijgt een groeiscore, top-10 verbeterpunten en 3 backlink-kansen. Daarna kunt u beslissen of Self-Service of Managed AI bij u past.',
  },
];

/* ─── Schema.org structured data ─── */
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.imperiaai.nl/" },
      { "@type": "ListItem", "position": 2, "name": "Prijzen", "item": "https://www.imperiaai.nl/prijzen" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "IMPERIA AI — AI Marketing Platform",
    "description": "AI-gestuurd marketingplatform voor MKB. Gratis scan, Connected Self-Service (€14,95 p/m) en Managed AI Bot (€995,95 launch / €2.495,95 recurring).",
    "brand": { "@type": "Brand", "name": "IMPERIA AI" },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "EUR",
      "lowPrice": "0",
      "highPrice": "2495.95",
      "offerCount": 4,
      "offers": [
        { "@type": "Offer", "name": "Gratis Online Scan", "price": "0", "priceCurrency": "EUR" },
        { "@type": "Offer", "name": "Connected Self-Service", "price": "14.95", "priceCurrency": "EUR" },
        { "@type": "Offer", "name": "Managed AI Bot (Launch)", "price": "995.95", "priceCurrency": "EUR" },
        { "@type": "Offer", "name": "Managed AI Bot (Recurring)", "price": "2495.95", "priceCurrency": "EUR" },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  },
];

export default function PrijzenPage() {
  return (
    <>
      <SEOHead
        title="Prijzen AI Marketing Manager voor MKB · Gratis Scan, €14,95 of Managed AI"
        description="Eerlijke prijzen voor uw AI Marketing Manager: gratis online groeiscan, Connected Self-Service vanaf €14,95 p/m excl. btw, of Managed AI Bot vanaf €995,95 excl. btw eerste maand. Maandelijks opzegbaar — ideaal voor zzp en mkb."
        canonical="/prijzen"
        keywords="prijzen ai marketing manager mkb, kosten online marketing zzp, managed ai bot prijs, ai seo abonnement, betaalbare marketing mkb, lokale seo prijs nederland"
        schema={SCHEMA}
      />

      {/* ═══ HERO ═══ */}
      <section style={{
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
        padding: '90px 0 70px', color: 'white',
      }}>
        <div className="container-custom max-w-4xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 bg-[#D4A017]/15 border border-[#D4A017]/30 rounded-full px-3 py-1.5 mb-6">
            <Sparkles size={12} className="text-[#D4A017]" />
            <span className="text-[#D4A017] text-xs font-bold tracking-wide uppercase">Transparante prijsstructuur</span>
          </span>
          <h1
            data-testid="prijzen-hero-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-6"
            style={{ fontFamily: "'Sora', sans-serif" }}>
            Eerlijke prijzen. <span style={{ color: '#D4A017' }}>Geen verborgen kosten.</span>
          </h1>
          <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-6">
            Probeer gratis. Doe het zelf vanaf €14,95 p/m. Of laat onze AI Bot het werk doen vanaf €995,95 excl. btw eerste maand, daarna €2.495,95 p/m. Alle tiers maandelijks opzegbaar.
          </p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>{VAT_LINE}</p>
        </div>
      </section>

      {/* ═══ TIERS ═══ */}
      <section id="tiers" style={{ background: '#f8fafc', padding: '70px 0 60px' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-5">
            {TIERS.map((t) => (
              <div key={t.id} id={t.id} data-testid={`tier-${t.id}`} style={{
                background: t.popular ? 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)' : 'white',
                color: t.popular ? 'white' : '#0f172a',
                borderRadius: 18, padding: '28px 24px',
                border: t.popular ? '2px solid #D4A017' : '1px solid #e2e8f0',
                position: 'relative',
                boxShadow: t.popular ? '0 24px 50px -10px rgba(212,160,23,0.3)' : '0 2px 8px rgba(0,0,0,0.04)',
              }}>
                {t.popular && (
                  <span style={{
                    position: 'absolute', top: -12, right: 24,
                    background: '#D4A017', color: '#0f172a', padding: '4px 12px',
                    borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
                  }}>
                    MEEST GEKOZEN
                  </span>
                )}
                <div style={{ marginBottom: 18 }}>
                  <p style={{ fontSize: 11, color: t.popular ? '#D4A017' : t.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                    {t.label}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 36, fontWeight: 800 }}>{t.price}</span>
                    <span style={{ fontSize: 13, opacity: 0.7 }}>{t.period}</span>
                  </div>
                  {t.secondary && (
                    <p style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>{t.secondary}</p>
                  )}
                  <p style={{ fontSize: 13, marginTop: 10, lineHeight: 1.45, opacity: 0.92 }}>{t.tagline}</p>
                </div>

                <div style={{
                  background: t.popular ? 'rgba(212,160,23,0.08)' : '#f8fafc',
                  borderRadius: 10, padding: '10px 12px', marginBottom: 14,
                  border: t.popular ? '1px solid rgba(212,160,23,0.2)' : '1px solid #e2e8f0',
                }}>
                  <p style={{ fontSize: 10, color: t.popular ? '#D4A017' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 3 }}>
                    Voor wie
                  </p>
                  <p style={{ fontSize: 12, lineHeight: 1.5, opacity: 0.9 }}>{t.voorWie}</p>
                </div>

                <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
                  {t.bullets.map((b, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, lineHeight: 1.5 }}>
                      <CheckCircle2 size={14} style={{ color: t.popular ? '#D4A017' : t.accent, flexShrink: 0, marginTop: 2 }} />
                      <span style={{ opacity: 0.92 }}>{b}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  data-testid={`tier-cta-${t.id}`}
                  to={t.cta.href}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '13px 20px', borderRadius: 10,
                    background: t.popular ? '#D4A017' : t.accent,
                    color: t.popular ? '#0f172a' : 'white',
                    fontSize: 14, fontWeight: 700, textDecoration: 'none',
                  }}>
                  {t.cta.label} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-[#94a3b8] mt-8" data-testid="pricing-vat-line">{VAT_LINE}</p>
        </div>
      </section>

      {/* ═══ MANAGED AI 2-PHASE explainer ═══ */}
      <section style={{ background: 'white', padding: '60px 0' }}>
        <div className="container-custom max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Hoe Managed AI prijs werkt
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
              €995,95 eerste maand, daarna €2.495,95 per maand
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div style={{ background: '#f8fafc', borderRadius: 14, padding: '24px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Zap size={20} style={{ color: '#1d4ed8' }} />
                </div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
                  Maand 1 — Launch <span style={{ color: '#1d4ed8' }}>€995,95</span>
                </h3>
              </div>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  'Volledige onboarding met strategie-call (60 min)',
                  'Kanaalkoppelingen (CMS, Google, Meta, GA4)',
                  'Volledige technische SEO-audit + first-fixes',
                  'Eerste 8-10 backlinks gepland',
                  'Ads-campagnes opgezet + getest',
                  'Google Bedrijfsprofiel volledig geoptimaliseerd',
                  '4 AI-content artikelen geschreven & gepubliceerd',
                ].map((b, i) => (
                  <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#374151', lineHeight: 1.55 }}>
                    <CheckCircle2 size={14} style={{ color: '#1d4ed8', flexShrink: 0, marginTop: 2 }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ background: '#f8fafc', borderRadius: 14, padding: '24px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(212,160,23,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Crown size={20} style={{ color: '#D4A017' }} />
                </div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
                  Maand 2+ — Recurring <span style={{ color: '#D4A017' }}>€2.495,95 p/m</span>
                </h3>
              </div>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  'Continue AI-uitvoering van fixes',
                  '25-40 nieuwe backlinks per maand',
                  'Ads-beheer met dagelijkse optimalisatie',
                  '4-8 AI-content artikelen per maand',
                  'Wekelijkse GBP-posts + reviews-respons',
                  'Maandelijks rapport + strategiegesprek (30 min)',
                  'Prioriteit support binnen 4 uur',
                ].map((b, i) => (
                  <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#374151', lineHeight: 1.55 }}>
                    <CheckCircle2 size={14} style={{ color: '#D4A017', flexShrink: 0, marginTop: 2 }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div style={{ background: '#fff7ed', borderLeft: '3px solid #fb923c', borderRadius: '0 10px 10px 0', padding: '14px 16px', marginTop: 18 }}>
            <p style={{ fontSize: 13, color: '#9a3412', lineHeight: 1.55 }}>
              <strong>Beide maanden zijn maandelijks opzegbaar.</strong> Geen jaarcontracten. Wij verdienen ons abonnement elke maand opnieuw — door echt resultaat te leveren.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ BACKLINKS PREVIEW WIDGET ═══ */}
      <BacklinksPreviewWidget />

      {/* ═══ VERGELIJKINGSTABEL ═══ */}
      <section id="vergelijken" style={{ background: '#f8fafc', padding: '70px 0' }}>
        <div className="container-custom max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Vergelijking
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
              Welke tier krijgt welke dienst?
            </h2>
          </div>
          <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#0f172a', color: 'white' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700 }}>Dienst</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700 }}>Gratis</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, background: '#1e1b4b' }}>€14,95</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, background: '#D4A017', color: '#0f172a' }}>Managed AI</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_ROWS.map((row, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f1f5f9', background: i % 2 === 1 ? '#fafbfc' : 'white' }}>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: '#0f172a', fontWeight: 600 }}>{row.feature}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'center', fontSize: 12, color: row.free === '–' ? '#cbd5e1' : '#475569' }}>{row.free}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'center', fontSize: 12, color: row.self === '–' ? '#cbd5e1' : '#2563eb', fontWeight: row.self !== '–' ? 600 : 400 }}>{row.self}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'center', fontSize: 12, color: '#D4A017', fontWeight: 700 }}>{row.managed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section style={{ background: 'white', padding: '80px 0 70px' }}>
        <div className="container-custom max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Veelgestelde vragen over prijzen
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
              Antwoorden op uw prijs-vragen
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {FAQ.map((f, i) => (
              <details key={i} data-testid={`prijzen-faq-${i}`} style={{
                background: '#f8fafc', borderRadius: 12, padding: '14px 18px',
                border: '1px solid #e2e8f0',
              }}>
                <summary style={{
                  cursor: 'pointer', fontWeight: 700, color: '#0f172a',
                  fontSize: 14, fontFamily: "'Sora',sans-serif",
                  listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                }}>
                  {f.q}
                  <ChevronRight size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
                </summary>
                <p style={{ marginTop: 10, fontSize: 13, color: '#475569', lineHeight: 1.65 }}>{f.a}</p>
              </details>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: '#64748b' }}>
            Andere vraag? <Link to="/contact" style={{ color: '#D4A017', fontWeight: 700, textDecoration: 'none' }}>Neem contact op →</Link>
          </p>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section style={{ background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', padding: '80px 0', color: 'white' }}>
        <div className="container-custom max-w-3xl mx-auto px-4 text-center">
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800, lineHeight: 1.15, marginBottom: 10 }}>
            Begin met de <span style={{ color: '#D4A017' }}>gratis scan</span>
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', marginBottom: 28, maxWidth: 520, margin: '0 auto 28px', lineHeight: 1.55 }}>
            Geen creditcard. Geen verplichtingen. Binnen 60 seconden ziet u uw groeiscore + top-10 verbeterpunten.
          </p>
          <Link to="/register" style={{
            background: '#D4A017', color: '#0f172a', padding: '14px 28px',
            borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            Start gratis scan <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </>
  );
}
